import { Command as CommandPrimitive } from "cmdk";
import { Check } from "lucide-react";
import { forwardRef, useMemo, useState } from "react";

import { cn } from "@/lib/utils";

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "./command";
import { Input } from "./input";
import { Popover, PopoverTrigger, PopoverContent } from "./popover";
import { Skeleton } from "./skeleton";

interface Props<T extends string> {
  selectedValue: T;
  onSelectedValueChange: (value: T) => void;
  searchValue: string;
  onSearchValueChange: (value: string) => void;
  items: { value: T; label: string }[];
  isLoading?: boolean;
  emptyMessage?: string;
  placeholder?: string;
  enableManualEntry?: boolean;
  onManualEntrySelected?: () => void;
  manualEntryText?: string;
  label?: string;
}

const AutoComplete = forwardRef<HTMLInputElement, Props<string>>(
  (
    {
      selectedValue,
      onSelectedValueChange,
      searchValue,
      onSearchValueChange,
      items,
      isLoading,
      emptyMessage = "No items.",
      placeholder = "Search...",
      enableManualEntry,
      manualEntryText,
      label,
    },
    ref
  ) => {
    const [open, setOpen] = useState(false);

    const labels = useMemo(
      () =>
        items.reduce(
          (acc, item) => {
            acc[item.value] = item.label;
            return acc;
          },
          {} as Record<string, string>
        ),
      [items]
    );

    const reset = () => {
      onSelectedValueChange("");
      onSearchValueChange("");
    };

    const onInputBlur = () => {
      setOpen(false);
      /* if (
        !e.relatedTarget?.hasAttribute("cmdk-list") &&
        labels[selectedValue] !== searchValue
      ) {
        reset();
      } */
    };

    const onSelectItem = (inputValue: string) => {
      if (inputValue === selectedValue) {
        reset();
      } else if (items.length === 0) {
        setOpen(false);
      }

      onSelectedValueChange(inputValue);
      onSearchValueChange(labels[inputValue] ?? "");
    };

    return (
      <div className="flex items-center">
        <Popover open={open} onOpenChange={setOpen}>
          <Command shouldFilter={false} label={label}>
            <PopoverTrigger asChild>
              <CommandPrimitive.Input
                asChild
                value={searchValue}
                onValueChange={onSearchValueChange}
                onKeyDown={(e) => setOpen(e.key !== "Escape")}
                onBlur={onInputBlur}
                ref={ref}
              >
                <Input placeholder={placeholder} />
              </CommandPrimitive.Input>
            </PopoverTrigger>
            {!open && <CommandList aria-hidden="true" className="hidden" />}
            <PopoverContent
              asChild
              onOpenAutoFocus={(e) => e.preventDefault()}
              onInteractOutside={(e) => {
                if (
                  e.target instanceof Element &&
                  e.target.hasAttribute("cmdk-input")
                ) {
                  e.preventDefault();
                }
              }}
              className="w-(--radix-popover-trigger-width) p-0"
            >
              <CommandList>
                {isLoading && (
                  <CommandPrimitive.Loading>
                    <div className="p-1">
                      <Skeleton className="h-6 w-full" />
                    </div>
                  </CommandPrimitive.Loading>
                )}
                {items.length > 0 && !isLoading ? (
                  <CommandGroup>
                    {items.map((option) => (
                      <ListItem
                        key={option.value}
                        onSelectItem={onSelectItem}
                        selectedValue={selectedValue}
                        searchValue={searchValue}
                        option={option}
                      />
                    ))}
                  </CommandGroup>
                ) : null}

                {enableManualEntry && (
                  <CommandGroup>
                    <CommandItem
                      className="cursor-pointer bg-neutral-300"
                      onSelect={() => {
                        setOpen(false);
                      }}
                    >
                      {manualEntryText}
                    </CommandItem>
                  </CommandGroup>
                )}

                {!isLoading ? (
                  <CommandEmpty>{emptyMessage ?? "No items."}</CommandEmpty>
                ) : null}
              </CommandList>
            </PopoverContent>
          </Command>
        </Popover>
      </div>
    );
  }
);

AutoComplete.displayName = "AutoComplete";

interface ListItemProps {
  option: { value: string; label: string };
  selectedValue: string;
  onSelectItem: (value: string) => void;
  searchValue: string;
}

const ListItem = (props: ListItemProps) => {
  const { option, selectedValue, onSelectItem, searchValue } = props;
  const regex = new RegExp(
    `(.*)(${searchValue?.toString().replace(/\W/g, "")})(.*)`,
    "i"
  );

  const result = option.label.match(regex);
  const [before, match, after] = result?.slice(1) ?? ["", "", ""];

  return (
    <CommandItem
      className="cursor-pointer"
      key={option.value}
      value={option.value}
      onMouseDown={(e) => e.preventDefault()}
      onSelect={onSelectItem}
    >
      <Check
        className={cn(
          "mr-2 h-4 w-4",
          selectedValue === option.value ? "opacity-100" : "opacity-0"
        )}
      />
      <span>
        {result?.length === 4 ? (
          <>
            {before}
            <strong>{match}</strong>
            {after}
          </>
        ) : (
          option.label
        )}
      </span>
    </CommandItem>
  );
};

export { AutoComplete };
